/**
 * `covomo_portal_search` theme: renders the real search mask inside the Covomo
 * portal ("Vergleichsrechner Beta") so a broker can preselect search parameters
 * before generating a calculator link / embed. Unlike the tariff-manager theme
 * this never runs a tariff search — instead it emits the chosen parameters back
 * to the embedding portal via a versioned postMessage protocol.
 */

export const COVOMO_PORTAL_SEARCH_THEME = "covomo_portal_search";

/** Bump when the message shape changes so the portal can guard on `version`. */
export const PORTAL_SEARCH_MESSAGE_VERSION = 1;

export const PORTAL_SEARCH_PREVIEW_TYPE = "covomo.search-params.preview";
export const PORTAL_SEARCH_SUBMIT_TYPE = "covomo.search-params.submit";

export type PortalSearchMessageKind = "preview" | "submit";

/**
 * Technical/context params that must never leak into the exported search query.
 * These are affiliate/booking/theme wiring, not user-selected search criteria.
 */
const TECHNICAL_PARAM_KEYS = new Set<string>([
  "r",
  "theme",
  "preview",
  "partner_token",
  "campaign",
  "sorting",
  "code",
  "a",
  "affiliate_id",
  "external_reference",
  "source",
  "source_reference",
  "context",
  "embed_method",
  "origin",
  "legacy_id",
  "vertrag_erneuern",
  "lang",
]);

export function resolveThemeKey(theme: unknown): string | undefined {
  if (theme == null) return undefined;
  return Array.isArray(theme) ? String(theme[0]) : String(theme);
}

export function isCovomoPortalSearchTheme(theme: unknown): boolean {
  return resolveThemeKey(theme) === COVOMO_PORTAL_SEARCH_THEME;
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function normalizeValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== null && entry !== undefined && entry !== "")
      .map((entry) => String(entry))
      .join(",");
  }
  return String(value);
}

export interface PortalSearchParamsResult {
  params: Record<string, string>;
  query: string;
}

/**
 * Turns the app's `{ type, value }` search-value list into a clean
 * alias → value map plus an encoded query string, dropping technical params
 * and empty values.
 */
export function buildPortalSearchParams(
  values: Array<{ type?: unknown; value?: unknown }> | null | undefined,
): PortalSearchParamsResult {
  const params: Record<string, string> = {};

  (Array.isArray(values) ? values : []).forEach((item) => {
    if (!item || typeof item.type !== "string") return;
    const key = item.type;
    if (TECHNICAL_PARAM_KEYS.has(key)) return;
    if (isEmptyValue(item.value)) return;
    const normalized = normalizeValue(item.value);
    if (normalized === "") return;
    // Last write wins, mirroring the app's own query building.
    params[key] = normalized;
  });

  const search = new URLSearchParams();
  Object.keys(params).forEach((key) => search.append(key, params[key]));

  return { params, query: search.toString() };
}

export interface PortalSearchMessage {
  type: typeof PORTAL_SEARCH_PREVIEW_TYPE | typeof PORTAL_SEARCH_SUBMIT_TYPE;
  version: number;
  query: string;
  params: Record<string, string>;
}

export function buildPortalSearchMessage(
  kind: PortalSearchMessageKind,
  values: Array<{ type?: unknown; value?: unknown }> | null | undefined,
): PortalSearchMessage {
  const { params, query } = buildPortalSearchParams(values);
  return {
    type:
      kind === "submit"
        ? PORTAL_SEARCH_SUBMIT_TYPE
        : PORTAL_SEARCH_PREVIEW_TYPE,
    version: PORTAL_SEARCH_MESSAGE_VERSION,
    query,
    params,
  };
}
